import React, {
	useState,
	forwardRef,
	useMemo,
	useEffect,
	useCallback,
	useRef,
} from "react";
import { Paper, Typography, IconButton } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { styled } from "@mui/system";
import { format, isValid } from "date-fns";
import { theme, TimeInput } from "uikit";

import { StyledRow } from "../styles";

import { CalendarModal } from "./components";
import { CustomDatePickerLocale } from "./interface";

const HiddenDateInput = styled("input")({
	position: "absolute",
	opacity: 0,
	width: 0,
	height: 0,
	pointerEvents: "none",
});

const DatePartInput = styled("input")(
	({ "data-size": size, disabled }: any) => {
		let width = "2.1ch";
		if (size === "year") width = "4.1ch";
		if (size === "monthFromPlaceholder") width = "2.9ch";

		return {
			width,
			fontSize: 14,
			border: "none",
			outline: "none",
			background: "transparent",
			textAlign: "center",
			appearance: "textfield",
			padding: "2px 0px",
			borderRadius: 4,
			color: disabled ? theme.colors.disabled_text : "inherit",
			cursor: disabled ? "not-allowed" : "text",
			transition: "background-color 0.2s, color 0.2s",

			"&:focus": {
				backgroundColor: disabled
					? "transparent"
					: "rgba(3, 169, 244, 0.3)",
				color: disabled ? theme.colors.disabled_text : "black",
			},

			"&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
				appearance: "none",
				margin: 0,
			},
		};
	},
);

const Calendar = forwardRef<HTMLInputElement, CalendarNamespace.Props>(
	(
		{
			value,
			onChange,
			minDate,
			maxDate,
			locale,
			width = 130,
			disabled = false,
			error = false,
			placeholder = false,
			withTime = false,
			step,
		},
		ref,
	) => {
		const [open, setOpen] = useState(false);
		const [time, setTime] = useState<TimeInput.Value>(value ?? new Date());

		const dayRef = useRef<HTMLInputElement>(null);
		const monthRef = useRef<HTMLInputElement>(null);
		const yearRef = useRef<HTMLInputElement>(null);
		const isEditingRef = useRef(false);

		const handleDayChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const raw = e.target.value.replace(/\D/g, "");

				if (!raw) {
					setDay("");
					return;
				}

				if (raw.length === 1) {
					setDay(raw);
					return;
				}

				if (raw.length === 2) {
					const num = parseInt(raw, 10);
					if (num >= 1 && num <= 31) {
						setDay(raw);
						monthRef.current?.focus();
					}
				}
			},
			[],
		);
		const handleMonthChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const raw = e.target.value.replace(/\D/g, "").slice(0, 2);

				if (!raw) {
					setMonth("");
					return;
				}

				const firstDigit = raw[0];
				const secondDigit = raw[1] ?? "";

				if (raw === "00") return;

				if (raw.length === 1) {
					if (["0", "1"].includes(firstDigit)) {
						setMonth(raw);
					}
					return;
				}

				const num = parseInt(raw, 10);
				if (num >= 1 && num <= 12) {
					setMonth(raw);
					yearRef.current?.focus();
				}
			},
			[],
		);

		const internalDate = useMemo(() => {
			const placeholderDate = placeholder === true ? new Date() : null;

			return value ?? placeholderDate;
		}, [placeholder, value]);

		const [day, setDay] = useState(() =>
			value && isValid(value)
				? String(value.getDate()).padStart(2, "0")
				: "",
		);
		const [month, setMonth] = useState(() =>
			value && isValid(value)
				? String(value.getMonth() + 1).padStart(2, "0")
				: "",
		);
		const [year, setYear] = useState(() =>
			value && isValid(value) ? String(value.getFullYear()) : "",
		);

		useEffect(() => {
			if (isEditingRef.current) return;

			if (value && isValid(value)) {
				setDay(String(value.getDate()).padStart(2, "0"));
				setMonth(String(value.getMonth() + 1).padStart(2, "0"));
				setYear(String(value.getFullYear()));
				if (withTime) {
					setTime(value);
				}
			} else {
				setDay("");
				setMonth("");
				setYear("");
				if (withTime) {
					setTime(new Date());
				}
			}
		}, [value, withTime]);

		const handleOpen = useCallback(() => setOpen(true), []);

		const handleClose = useCallback(() => setOpen(false), []);

		const handleSubmit = useCallback(
			(date: Date) => {
				const newDate = new Date(date);

				if (withTime) {
					const base = value && isValid(value) ? value : time;
					if (base instanceof Date && isValid(base)) {
						newDate.setHours(base.getHours());
						newDate.setMinutes(base.getMinutes());
						newDate.setSeconds(base.getSeconds());
					}
				}

				onChange?.(newDate);
				handleClose();
			},
			[onChange, handleClose, withTime, value, time],
		);

		const handleFocusSelect = useCallback(
			(e: React.FocusEvent<HTMLInputElement>) => {
				isEditingRef.current = true;
				setTimeout(() => e.target.select(), 0);
			},
			[],
		);
		const handleBlur = useCallback(() => {
			isEditingRef.current = false;

			const d = parseInt(day, 10);
			const m = parseInt(month, 10);
			const y = parseInt(year, 10);

			if (!day && !month && !year) return;

			if (d > 0 && m > 0 && y >= 1000) {
				const newDate = new Date(y, m - 1, d);

				if (withTime && time instanceof Date && isValid(time)) {
					newDate.setHours(time.getHours());
					newDate.setMinutes(time.getMinutes());
					newDate.setSeconds(time.getSeconds());
				}

				if (isValid(newDate)) {
					onChange?.(newDate);
				}
			}
		}, [day, month, year, time, withTime, onChange]);
		const handleYearChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				const raw = e.target.value.replace(/\D/g, "");
				let trimmed = raw.slice(0, 4);

				if (trimmed.length === 1 && !/[12]/.test(trimmed[0])) {
					trimmed = "";
				}

				setYear(trimmed);

				if (trimmed.length === 4) {
					const num = parseInt(trimmed, 10);
					if (num >= 1900 && num <= 2100) {
						const d = parseInt(day, 10);
						const m = parseInt(month, 10);

						if (d > 0 && m > 0) {
							const newDate = new Date(num, m - 1, d);

							if (
								withTime &&
								time instanceof Date &&
								isValid(time)
							) {
								newDate.setHours(time.getHours());
								newDate.setMinutes(time.getMinutes());
								newDate.setSeconds(time.getSeconds());
							}

							if (isValid(newDate)) {
								onChange?.(newDate);
							}
						}
						e.target.blur();
					}
				}
			},
			[day, month, onChange, time, withTime],
		);

		return (
			<StyledRow>
				<Paper
					variant="outlined"
					sx={() => {
						let bgColor = "white";
						let textColor = "inherit";
						let borderColor: string | undefined;

						if (disabled) {
							bgColor = theme.colors.disabled_filled;
							textColor = theme.colors.disabled_text;
							borderColor = theme.colors.disabled_filled;
						}

						if (error) {
							borderColor = theme.colors.error;
						}

						const hoverBorderColor = disabled
							? theme.colors.disabled_filled
							: theme.colors.primary;

						return {
							width,
							height: 32,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "6px 9px 6px 12px",
							boxSizing: "border-box",
							gap: 1,
							cursor: disabled ? "not-allowed" : "pointer",

							backgroundColor: bgColor,
							color: textColor,

							borderColor,
							...(withTime
								? {
										borderTopRightRadius: 0,
										borderBottomRightRadius: 0,
										borderRight: 0,
								  }
								: {}),

							"&:hover": {
								borderColor: hoverBorderColor,
								...(withTime
									? {
											borderRight: `1px solid ${hoverBorderColor}`,
											padding: "6px 8px 6px 12px",
									  }
									: {}),
							},
						};
					}}
				>
					<StyledRow alignItems="center">
						<DatePartInput
							ref={dayRef}
							data-size="day"
							value={day}
							onChange={handleDayChange}
							placeholder="dd"
							inputMode="numeric"
							disabled={disabled}
							onFocus={handleFocusSelect}
							onBlur={handleBlur}
						/>
						<Typography>.</Typography>
						<DatePartInput
							ref={monthRef}
							data-size={month ? "day" : "monthFromPlaceholder"}
							value={month}
							onChange={handleMonthChange}
							placeholder="mm"
							inputMode="numeric"
							disabled={disabled}
							onFocus={handleFocusSelect}
							onBlur={handleBlur}
						/>
						<Typography>.</Typography>
						<DatePartInput
							ref={yearRef}
							data-size="year"
							value={year}
							onChange={handleYearChange}
							placeholder="yyyy"
							inputMode="numeric"
							disabled={disabled}
							onFocus={handleFocusSelect}
							onBlur={handleBlur}
						/>
					</StyledRow>

					<IconButton
						size="small"
						sx={{
							padding: 0,
							width: 16,
							height: 16,
							minWidth: 0,
							minHeight: 0,
							color: disabled
								? theme.colors.disabled_text
								: "inherit",
							cursor: disabled ? "not-allowed" : "pointer",
							"& svg": {
								width: 16,
								height: 16,
							},
						}}
						onClick={(e) => {
							if (!disabled) {
								handleOpen();
								e.stopPropagation();
							}
						}}
						disabled={disabled}
					>
						<CalendarMonthIcon />
					</IconButton>
				</Paper>
				{withTime && (
					<TimeInput
						disabled={disabled}
						value={time}
						step={step}
						onChange={(newTime) => {
							if (
								value &&
								newTime instanceof Date &&
								isValid(value)
							) {
								const updated = new Date(value);
								updated.setHours(newTime.getHours());
								updated.setMinutes(newTime.getMinutes());
								updated.setSeconds(newTime.getSeconds());
								onChange?.(updated);
							}
							setTime(newTime);
						}}
						style={{
							width: "82px",
							borderRadius: "0px 5px 5px 0px !important",
						}}
					/>
				)}

				<CalendarModal
					open={open}
					onClose={handleClose}
					value={internalDate}
					onSubmit={handleSubmit}
					onCancel={handleClose}
					minDate={minDate}
					maxDate={maxDate}
					locale={locale}
				/>

				<HiddenDateInput
					ref={ref}
					type="date"
					value={
						value instanceof Date ? format(value, "yyyy-MM-dd") : ""
					}
					onChange={(e) => {
						const newDate = new Date(e.target.value);
						onChange?.(newDate);
					}}
					aria-hidden="true"
					tabIndex={-1}
					title="hidden date input"
				/>
			</StyledRow>
		);
	},
);

export declare namespace CalendarNamespace {
	type Value = Date | null | undefined;
	interface Props extends React.RefAttributes<HTMLInputElement> {
		value: Value;
		onChange: (val: Date) => void;
		minDate?: Date;
		maxDate?: Date;
		locale?: CustomDatePickerLocale;

		width?: number | string;
		disabled?: boolean;
		error?: boolean;
		withTime?: boolean; // * add component time
		step?: number;

		// * Compatibility with DatePicker
		autoFocus?: boolean;
		placeholder?: string | Date | boolean;
		floatContainerId?: string;
		iconColor?: string;
		inputRef?: React.Ref<HTMLInputElement>;
	}
}

export default Calendar;
